import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Drawer } from '@dhuttu/ui-components';
import { offlinePasswordSchema, type OfflinePasswordInput } from '../routes/cashiers.schema';
import type { UserView } from '../api/users';

/**
 * Set / rotate / clear the cashier's offline password.
 *
 * Two mutations live in this drawer: SET (PUT) and CLEAR (DELETE). Both are
 * lifted into props so the parent owns the underlying react-query mutation
 * and cache invalidation.
 */
export function OfflinePasswordDrawer({
  open, onClose, cashier, onSubmit, onClear, submitting,
}: {
  open: boolean;
  onClose: () => void;
  cashier: UserView | null;
  onSubmit: (password: string) => void;
  onClear: () => void;
  submitting?: boolean;
}) {
  const { register, handleSubmit, reset, formState: { errors } } = useForm<OfflinePasswordInput>({
    resolver: zodResolver(offlinePasswordSchema),
  });
  const [confirmClear, setConfirmClear] = useState(false);

  if (!open || !cashier) return null;

  const lastSetLabel = cashier.offlinePasswordSet && cashier.offlinePasswordUpdatedAt
    ? `Last set ${new Date(cashier.offlinePasswordUpdatedAt).toLocaleDateString()}`
    : 'Not set';

  return (
    <Drawer
      open={open}
      onClose={() => { reset(); setConfirmClear(false); onClose(); }}
      title={`Offline password — ${cashier.displayName}`}
    >
      <div className="flex flex-col gap-4">
        <div className="text-xs text-[color:var(--v2-ink-soft)]">
          <span className="font-semibold uppercase tracking-[0.16em] text-[color:var(--v2-mute)]">Status</span>
          <p className="mt-1 text-sm text-[color:var(--v2-ink)]">{lastSetLabel}</p>
        </div>

        <form
          onSubmit={handleSubmit((d) => onSubmit(d.password))}
          className="flex flex-col gap-3"
        >
          <label className="block text-sm">
            New password
            <input
              type="password"
              autoComplete="new-password"
              {...register('password')}
              className="mt-1 w-full border rounded p-2"
            />
          </label>
          {errors.password && <p className="text-sm text-accent">{errors.password.message}</p>}

          <label className="block text-sm">
            Confirm password
            <input
              type="password"
              autoComplete="new-password"
              {...register('confirm')}
              className="mt-1 w-full border rounded p-2"
            />
          </label>
          {errors.confirm && <p className="text-sm text-accent">{errors.confirm.message}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="self-start rounded bg-primary px-4 py-2 text-white disabled:opacity-60"
          >
            {submitting ? 'Saving…' : 'Save password'}
          </button>
        </form>

        <div className="pt-4 mt-2 border-t border-[color:var(--v2-stroke)]">
          {!confirmClear ? (
            <button
              type="button"
              onClick={() => setConfirmClear(true)}
              disabled={!cashier.offlinePasswordSet}
              className="text-sm text-rose-700 underline disabled:opacity-50 disabled:no-underline"
            >
              Clear offline password
            </button>
          ) : (
            <div className="flex flex-col gap-2">
              <p className="text-sm text-[color:var(--v2-ink)]">
                Clearing the offline password disables offline login for this cashier on POS terminals after their next sync. Continue?
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setConfirmClear(false); onClear(); }}
                  className="rounded bg-rose-600 px-3 py-1.5 text-white text-sm"
                >
                  Yes, clear it
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmClear(false)}
                  className="text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
}
